import { memo, useCallback, useMemo } from "react"
import { FlatList, ListRenderItemInfo, StyleSheet, Text, View } from "react-native"
import { ContactScheduleAnswerStackScreenProps } from "src/types.d"
import { Colors } from "src/constants/Colors"
import { ContactScheduleAnswerScreens } from "src/constants/ContactScheduleAnswerScreens"
import { ScheduleAnswerLabel, ScheduleAnswerType } from "src/constants/ScheduleAnswerType"
import { useContactScheduleAnswerDetailData } from "src/recoil/hooks/screens/useContactScheduleAnswerDetailData"
import { CustomRefreshControl } from "src/components/projects/CustomRefreshControl"
import { CustomKeyboardAvoidingView } from "src/components/parts/CustomKeyboardAvoidingView"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import dayjs from "dayjs"

export type ContactScheduleAnswerDetailParams = {
  contactId: string
  scheduleId: string
  scheduleAnswerType: ScheduleAnswerType
}

export const ContactScheduleAnswerDetail = memo<
  ContactScheduleAnswerStackScreenProps<typeof ContactScheduleAnswerScreens.ContactScheduleAnswerDetail>
>(
  ({
    route: {
      params: { contactId, scheduleId, scheduleAnswerType },
    },
  }) => {
    const { members, isLoading, refresher } = useContactScheduleAnswerDetailData(contactId, scheduleId, scheduleAnswerType)
    const insets = useSafeAreaInsets()

    const contentContainerStyle = useMemo(() => ({ paddingBottom: insets.bottom }), [insets.bottom])

    const ListHeaderComponent = useMemo(
      () => (
        <View style={styles.titleContainer}>
          <Text style={styles.title}>{ScheduleAnswerLabel[scheduleAnswerType]}</Text>
          <Text style={styles.subTitle}>{members.length}人</Text>
        </View>
      ),
      [members, scheduleAnswerType]
    )

    const RenderItem = useCallback(
      (itemInfo: ListRenderItemInfo<{ nickname: string; answeredAt: Date | undefined }>) => (
        <>
          <View style={styles.rowContainer}>
            <Text style={styles.rowText} numberOfLines={1} ellipsizeMode="tail">
              {itemInfo.item.nickname}
            </Text>
            {itemInfo.item.answeredAt ? (
              <View style={styles.datetimeContainer}>
                <Text style={styles.dateText}>{dayjs(itemInfo.item.answeredAt).format("YYYY/MM/DD")}</Text>
                <Text style={styles.timeText}>{dayjs(itemInfo.item.answeredAt).format("HH:mm")}</Text>
              </View>
            ) : null}
          </View>
          <View style={styles.rowSeparator} />
        </>
      ),
      []
    )

    return (
      <CustomKeyboardAvoidingView style={[styles.container]}>
        <FlatList
          data={members}
          renderItem={RenderItem}
          ListHeaderComponent={ListHeaderComponent}
          refreshControl={<CustomRefreshControl refreshing={isLoading} onRefresh={refresher} />}
          contentContainerStyle={contentContainerStyle}
        />
      </CustomKeyboardAvoidingView>
    )
  }
)

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.white3,
  },
  titleContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 13,
    paddingHorizontal: 22,
    borderBottomWidth: 1,
    borderBottomColor: Colors.lightGrey,
  },
  title: {
    fontSize: 18,
    fontWeight: "bold",
    color: Colors.greyshBrown,
  },
  subTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: Colors.orange,
  },
  rowContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 13,
    paddingHorizontal: 22,
  },
  rowText: {
    fontSize: 16,
    color: Colors.greyshBrown,
    flexShrink: 1,
  },
  datetimeContainer: {
    flexDirection: "row",
    marginLeft: 12,
  },
  dateText: {
    marginRight: 12,
    fontSize: 15,
  },
  timeText: {
    fontSize: 15,
  },
  rowSeparator: {
    width: "100%",
    height: 1,
    marginLeft: 22,
    backgroundColor: Colors.lightGrey,
  },
})
